import { TransformedXAxisType } from '@api';
import { useWindowResize } from '@helpers';
import { type EChartsType, getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import { useEffect, useRef, useState } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  SingleSeries,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { RemoteLegend } from '../RemoteLegend';
import {
  isDefaultLabelFormatterCallbackParams,
  tooltipFormatter,
} from '../utils';
import { formatAxis } from './utils';

export interface BasicBarChartProps extends ApacheChartBase {
  /** Categories rendered on the value axis (x-axis when vertical, y-axis when `isHorizontal`). */
  axis: TransformedXAxisType;
  /** Series to render — one bar group per entry. */
  series: SingleSeries[];
  /** Render bars horizontally instead of vertically. Defaults to `false`. */
  isHorizontal?: boolean;
  /** Labels for the chart axes used in exports: `[categoryAxisLabel, valueAxisLabel]` (e.g., `["Event Types", "Count"]`). */
  axisLabels?: string[];
}

/**
 * Apache ECharts bar chart with the design-system theming and a
 * separate `RemoteLegend` rendered alongside.
 *
 * Wraps `ApacheChart` with bar-specific defaults (formatter, grid,
 * legend off) and supports both vertical and horizontal orientations
 * via `isHorizontal`.
 */
export const BasicBarChart = ({
  theme = 'dark_default',
  axis,
  series,
  isHorizontal = false,
  axisLabels,
  onInit,
  isModal,
}: BasicBarChartProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const [legendInstance, setLegendInstance] = useState<EChartsType | null>(
    null
  );
  const { width, height } = useWindowResize({ targetRef: chartRef });

  useEffect(() => {
    if (chartRef.current) {
      const chartDom = getInstanceByDom(chartRef.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(chartRef.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    setLegendInstance(chartDom);
    const seriesData: Types.SeriesOption[] = series.map(i => ({
      name: i.name,
      data: i.data,
      type: 'bar',
    }));

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        ...formatAxis(axis, isHorizontal, {
          axisLabels,
          chartHeight: height || DEFAULT_CHART_HEIGHT,
          chartWidth,
        }),
        tooltip: {
          formatter: params => {
            if (isDefaultLabelFormatterCallbackParams(params)) {
              return tooltipFormatter({
                isShowTitle: false,
                isShowMarker: true,
                params,
              });
            }

            return '';
          },
        },
        grid: {
          bottom: '5%',
        },
        legend: { show: false },
        series: seriesData,
      },
    });

    chartDom.setOption(option);

    if (wrapperRef.current) {
      onInit?.(wrapperRef.current);
    }

    return () => {
      chartDom.dispose();
      setLegendInstance(null);
    };
  }, [
    theme,
    axis,
    series,
    isHorizontal,
    axisLabels,
    onInit,
    width,
    height,
    isModal,
  ]);

  return (
    <div ref={wrapperRef}>
      <div ref={chartRef} />
      {legendInstance && (
        <RemoteLegend
          theme={theme}
          legendData={series.map(s => ({ name: s.name, type: 'bar' }))}
          targetChart={legendInstance}
        />
      )}
    </div>
  );
};
